// src/pages/HotelDetail.jsx
import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { hotels } from "../data/hotels";
import "./Hotels.css";

export default function HotelDetail() {
  const { id } = useParams();

  // Find hotel
  const hotel = Array.isArray(hotels) ? hotels.find((h) => h.id === id) : null;

  // === Hooks at top-level ===
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    people: "",
  });

  // Early return
  if (!hotel) return <p>Hotel not found</p>;

  const longContent = hotel?.longContent ?? {};
  const introLines = (longContent.intro ?? "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  const prices = longContent?.prices ?? null;
  const detailsText = longContent?.details ?? "";

  // === Handlers ===
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleBook = (e) => {
    e.preventDefault();
    const message = encodeURIComponent(
      `Hello, I want to book: ${hotel.title}\n` +
      `Name: ${form.name}\n` +
      `Email: ${form.email}\n` +
      `Number of people: ${form.people}`
    );
    const whatsappLink = `https://wa.me/255718665620?text=${message}`;
    window.open(whatsappLink, "_blank");
  };

  return (
    <section className="hotel-detail-card">
      <div className="detail-layout">
        {/* Hero image */}
        {hotel.image && (
          <div className="detail-image-wrapper">
            <img
              src={hotel.image.startsWith("/") ? hotel.image : `/images/${hotel.image}`}
              alt={hotel.title}
              style={{
                width: "100%",
                height: "auto",
                borderRadius: 8,
                objectFit: "cover",
                display: "block",
              }}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/images/placeholder.png";
              }}
            />
          </div>
        )}

        {/* Text content */}
        <div className="detail-text">
          <h2>{hotel.title}</h2>
          {hotel.location && <p className="location">📍 {hotel.location}</p>}

          {introLines.length > 0 && (
            <div className="description">
              {introLines.map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
          )}

          {detailsText && (
            <div className="details">
              {detailsText
                .split("\n")
                .map((l) => l.trim())
                .filter(Boolean)
                .map((line, idx) => (
                  <p key={idx}>{line}</p>
                ))}
            </div>
          )}

          {/* Booking Form Modal */}
          {showForm && (
            <div
              className="modal-overlay"
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0,0,0,0.6)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1000,
              }}
              onClick={() => setShowForm(false)}
            >
              <div
                className="modal-content"
                style={{
                  backgroundColor: "#fff",
                  padding: 20,
                  borderRadius: 8,
                  minWidth: 300,
                  maxWidth: 400,
                  position: "relative",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <h3>Book This Tour</h3>
                <form
                  onSubmit={handleBook}
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    style={{ padding: 8, borderRadius: 4, border: "1px solid #ccc" }}
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email (optional)"
                    value={form.email}
                    onChange={handleChange}
                    style={{ padding: 8, borderRadius: 4, border: "1px solid #ccc" }}
                  />
                  <input
  type="number"
  name="people"
  placeholder="Number of people"
  min="1"
  value={form.people}
  onChange={handleChange}
  required
  style={{
    padding: 8,
    borderRadius: 4,
    border: "1px solid #ccc",
  }}
/>
                  <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                    <button
                      type="submit"
                      style={{
                        flex: 1,
                        padding: "10px",
                        backgroundColor: "#25D366",
                        color: "#fff",
                        border: "none",
                        borderRadius: 4,
                        fontWeight: "bold",
                        cursor: "pointer",
                      }}
                    >
                      Send via WhatsApp
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      style={{
                        flex: 1,
                        padding: "10px",
                        backgroundColor: "#ccc",
                        color: "#000",
                        border: "none",
                        borderRadius: 4,
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Prices table */}
          {prices && Array.isArray(prices.table) && (
            <div className="prices" style={{ marginTop: 20 }}>
              <h4>Prices ({prices.currency ?? "USD"})</h4>
              {prices.note && <p className="muted">{prices.note}</p>}
              <table className="prices-table" role="table">
                <thead>
                  <tr>
                    <th>Room Type</th>
                    <th>Occupancy</th>
                    <th>Rate ({prices.currency ?? "USD"})</th>
                  </tr>
                </thead>
                <tbody>
                  {prices.table.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.roomType}</td>
                      <td>{row.occupancy}</td>
                      <td>{row.rateUSD ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Book Tour Button */}
          <div style={{ marginTop: 20 }}>
            <button
              onClick={() => setShowForm(true)}
              style={{
                padding: "10px 20px",
                backgroundColor: "#25D366",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              Book Hotel
            </button>
          </div>

          {/* Actions */}
          <div className="detail-actions" style={{ marginTop: 20 }}>
            <Link to="/hotels" className="btn-back">
              ← Back to Hotels
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
